import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { type JSX, useState } from 'react';
import { useKeyboardShortcut } from 'ts/base/hooks/UseKeyboardShortcut';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { CompareCodeComponent } from 'ts/commons/code/compare/CompareCodeComponent';
import { type CompareContentInfo } from 'ts/commons/code/compare/CompareContentSide';
import { Links } from 'ts/commons/links/Links';
import { NavigationUtils } from 'ts/commons/NavigationUtils';
import { PathUtils } from 'ts/commons/PathUtils';
import { Button } from 'ts/components/Button';
import { Divider } from 'ts/components/Divider';
import { Dropdown } from 'ts/components/Dropdown';
import { Grid, GridColumn } from 'ts/components/Grid';
import { SpecItemCompareContainer } from 'ts/perspectives/compare/SpecItemCompareView';
import { getSpecItemFindingDisplayMessage } from 'ts/perspectives/findings/details/FindingBasedSpecItemLink';
import {
	SiblingBatchBlacklistButtonGroup,
	SiblingSingleActionButtonGroup,
	SiblingSingleBlacklistButtonGroup
} from 'ts/perspectives/findings/details/tabs/FindingDetailsSiblingsTabButtonGroups';
import { FindingsUtils } from 'ts/perspectives/findings/FindingsUtils';
import { getFindingLocationDescription } from 'ts/perspectives/quality_control/reports/slides/parameters/findings-detail-slide/SiblingSelectionModal';
import { useSpecItemsFromFindings } from 'ts/perspectives/requirement_tracing/SpecItemUtils';
import type { ElementLocation } from 'typedefs/ElementLocation';
import type { SpecItem } from 'typedefs/SpecItem';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import type { TrackedFindingWithDiffInfo } from 'typedefs/TrackedFindingWithDiffInfo';

/** Props for FindingDetailsSiblingsTabContent. */
type FindingDetailsSiblingsTabContentProps = {
	finding: TrackedFindingWithDiffInfo;
	siblingFindings: TrackedFinding[];
	commit: UnresolvedCommitDescriptor;
};

/** The tab of the finding details that shows a table with the finding's siblings (e.g. other clone instances). */
export function FindingDetailsSiblingsTabContent({
	finding,
	siblingFindings,
	commit
}: FindingDetailsSiblingsTabContentProps): JSX.Element {
	// If the finding no longer exists, the sibling tab's commit needs to be set to the parent of the finding's death commit.
	let currentOrDeathCommit = commit;
	if (finding.death != null) {
		currentOrDeathCommit = UnresolvedCommitDescriptor.getPreviousCommit(
			UnresolvedCommitDescriptor.wrap(finding.death)
		);
	}
	const projectId = useProjectId();
	useKeyboardShortcut('V', 'Compare with sibling', () => {
		const href = getComparisonLink(
			finding.location,
			siblingFindings[0]!.location,
			currentOrDeathCommit,
			projectId,
			finding.id,
			siblingFindings[0]!.id
		);
		NavigationUtils.updateLocation(href!);
	});

	const associatedSpecItemsById = useSpecItemsFromFindings(projectId, siblingFindings, commit);
	return (
		<>
			<Grid stackable padded columns={2}>
				<GridColumn width={6}>
					<h2>Sibling findings</h2>
				</GridColumn>
				<GridColumn width={10} textAlign="right">
					<SiblingBatchBlacklistButtonGroup
						commit={currentOrDeathCommit}
						finding={finding}
						siblingFindings={siblingFindings}
					/>
				</GridColumn>
			</Grid>
			<SiblingsDropdown
				commit={currentOrDeathCommit}
				finding={finding}
				siblingFindings={siblingFindings}
				associatedSpecItemsById={associatedSpecItemsById}
			/>
		</>
	);
}

type SiblingsDropdownProps = {
	siblingFindings: TrackedFinding[];
	commit: UnresolvedCommitDescriptor;
	finding: TrackedFindingWithDiffInfo;
	associatedSpecItemsById: Record<string, SpecItem>;
};

/**
 * The SiblingsDropdown component consist of the sibling dropdown selection, buttons to modify the selected sibling's
 * state and the inline code comparison component.
 */
function SiblingsDropdown({ siblingFindings, commit, finding, associatedSpecItemsById }: SiblingsDropdownProps) {
	const siblingOptions = siblingFindings.map(siblingFinding => ({
		key: siblingFinding.id,
		value: siblingFinding.id,
		text: getSiblingLinkText(finding, siblingFinding, associatedSpecItemsById)
	}));
	const [selectedSibling, setSelectedSibling] = useState(siblingFindings[0]!);
	return (
		<>
			<Divider section />
			<div className="flex gap-2">
				<div className="grow">
					<Dropdown
						options={siblingOptions}
						selection
						fluid
						search
						defaultValue={siblingOptions[0]!.value}
						onChange={(event, data) => {
							setSelectedSibling(siblingFindings.find(sibling => sibling.id === data.value)!);
						}}
						data-testid="finding-siblings-dropdown"
					/>
				</div>
				<div className="items-center">
					<SiblingSingleActionButtonGroup commit={commit} finding={finding} sibling={selectedSibling} />
				</div>
				<div className="items-center">
					<SiblingSingleBlacklistButtonGroup sibling={selectedSibling} commit={commit} />
				</div>
			</div>
			<Divider section />
			{FindingsUtils.isSpecItemFindingLocation(finding.location) ? (
				<SpecItemCodeComparison sibling={selectedSibling} finding={finding} commit={commit} />
			) : (
				<InlineCodeComparison finding={finding} sibling={selectedSibling} commit={commit} />
			)}
		</>
	);
}

function getSiblingLinkText(
	finding: TrackedFindingWithDiffInfo,
	siblingFinding: TrackedFinding,
	associatedSpecItemsById: Record<string, SpecItem>
): string {
	if (
		FindingsUtils.isSpecItemFindingLocation(siblingFinding.location) &&
		// Can happen, while the items are still loaded
		associatedSpecItemsById[siblingFinding.location.issueId] !== undefined
	) {
		return getSpecItemFindingDisplayMessage(
			associatedSpecItemsById[siblingFinding.location.issueId]!,
			siblingFinding.location
		);
	}
	return getFindingLocationDescription(finding, siblingFinding.location);
}

/** The props for the Inline Code Comparison */
type InlineCodeComparisonProps = {
	finding: TrackedFindingWithDiffInfo;
	sibling: TrackedFinding;
	commit: UnresolvedCommitDescriptor;
};

/** The code comparison component for comparing a finding to one of its siblings. */
function InlineCodeComparison({ finding, sibling, commit }: InlineCodeComparisonProps) {
	const project = useProjectId();
	const leftContent = compareContentInfoFromLocation(project, commit, finding.location);
	const rightContent = compareContentInfoFromLocation(project, commit, sibling.location);
	return (
		<CompareCodeComponent
			scrollIntoViewOnMount
			maxCodeHeight="40em"
			leftCompareInfo={leftContent}
			rightCompareInfo={rightContent}
		/>
	);
}

function compareContentInfoFromLocation(
	project: string,
	commit: UnresolvedCommitDescriptor,
	location: ElementLocation
): CompareContentInfo {
	let region: { startLine: number; endLine: number } | undefined;
	if (PathUtils.isTextRegionLocation(location)) {
		region = { startLine: location.rawStartLine, endLine: location.rawEndLine };
	}
	return {
		project,
		commit,
		uniformPath: location.uniformPath,
		region,
		initialLine: region?.startLine
	};
}

function SpecItemCodeComparison({ finding, sibling, commit }: InlineCodeComparisonProps) {
	const project = useProjectId();
	if (
		FindingsUtils.isSpecItemFindingLocation(finding.location) &&
		FindingsUtils.isSpecItemFindingLocation(sibling.location)
	) {
		const left = { findingId: finding.id, issueId: finding.location.issueId };
		const right = { findingId: sibling.id, issueId: sibling.location.issueId };
		return <SpecItemCompareContainer project={project} commit={commit} left={left} right={right} />;
	}
	return null;
}

/**
 * Returns the link to the full screen finding siblings comparison view. The link is suppressed if one of the locations
 * is a plain element location (i.e. no region information).
 *
 * @param project The projectID
 * @param leftFindingLocation The location of the left sibling
 * @param rightFindingLocation The location of the right sibling
 * @returns Link as string or null if the link is suppressed
 */
export function getComparisonLink(
	leftFindingLocation: TrackedFinding['location'],
	rightFindingLocation: TrackedFinding['location'],
	commit: UnresolvedCommitDescriptor,
	project: string,
	leftFindingId?: string,
	rightFindingId?: string
): string | null {
	if (
		leftFindingId &&
		rightFindingId &&
		FindingsUtils.isSpecItemFindingLocation(leftFindingLocation) &&
		FindingsUtils.isSpecItemFindingLocation(rightFindingLocation)
	) {
		return Links.compareSpecItems(
			project,
			{
				issueId: leftFindingLocation.issueId,
				findingId: leftFindingId
			},
			{
				issueId: rightFindingLocation.issueId,
				findingId: rightFindingId
			},
			commit
		);
	}
	const rightLocation = rightFindingLocation;
	if (PathUtils.isTextRegionLocation(leftFindingLocation) && PathUtils.isTextRegionLocation(rightLocation)) {
		return Links.compareFiles(
			project,
			{
				uniformPath: leftFindingLocation.uniformPath,
				commit,
				startLine: leftFindingLocation.rawStartLine,
				endLine: leftFindingLocation.rawEndLine
			},
			{
				uniformPath: rightLocation.uniformPath,
				commit,
				startLine: rightLocation.rawStartLine,
				endLine: rightLocation.rawEndLine
			}
		);
	} else if (
		PathUtils.isQualifiedNameLocation(leftFindingLocation) &&
		PathUtils.isQualifiedNameLocation(rightLocation)
	) {
		return Links.modelCompare(project, {
			t: commit,
			'left-path': leftFindingLocation.uniformPath,
			'left-location': leftFindingLocation.qualifiedName,
			'right-path': rightLocation.uniformPath,
			'right-location': rightLocation.qualifiedName
		});
	}
	return null;
}

type SiblingComparisonLinkProps = {
	project: string;
	/** The location of the "left" sibling */
	leftFindingLocation: TrackedFinding['location'];
	/** The location of the "right" sibling */
	rightFindingLocation: TrackedFinding['location'];
	commit: UnresolvedCommitDescriptor;
};

/**
 * Displays a link to compare two finding siblings with each other. The link is suppressed if one of the locations is a
 * plain element location (i.e. no region information).
 */
export function SiblingComparisonLink({
	project,
	leftFindingLocation,
	rightFindingLocation,
	commit
}: SiblingComparisonLinkProps): JSX.Element | null {
	const link = getComparisonLink(leftFindingLocation, rightFindingLocation, commit, project);
	if (!link) {
		return null;
	}
	return <Button basic size="small" as={TeamscaleLink} content="Compare" to={link} />;
}
